<script context="module" lang="ts">
  export type Edge = "start" | "end" | "middle";
  export type Align = "left" | "center" | "right";
</script>

<script lang="ts">
  import { fade } from "svelte/transition";
  import { CELL_FADE_BASE_ANIM_CONFIG } from "./constants";
  import Icon from "../icons/Icon.svelte";
  import type { SortDirection } from "@tanstack/svelte-table";
  import type { HTMLAttributes } from "svelte/elements";

  interface $$Props extends HTMLAttributes<HTMLDivElement> {
    sticky?: boolean;
    align?: Align;
    edge?: Edge;
    colIndex?: number;
    sortable?: boolean;
    sortDirection?: SortDirection | false;
    colWidth?: number;
    divElement?: HTMLElement | null;
    shadow?: boolean;
    gradientFade?: "start" | "none";
  }

  export let sticky = true;
  export let shadow = false;
  export let align: Align = "left";
  export let edge: Edge = "middle";
  export let colIndex: number = 0;
  export let sortable: boolean = false;
  export let sortDirection: SortDirection | false = false;
  export let colWidth = 0;
  export let divElement: HTMLElement | null = null;
  export let gradientFade: "start" | "none" = "none";

  function handleKeyPress(
    e: KeyboardEvent & {
      currentTarget: EventTarget & HTMLDivElement;
    }
  ) {
    if (e.key === "Enter") {
      e.currentTarget.click();
    }
  }
</script>

<div
  {...$$restProps}
  role="columnheader"
  class="base"
  class:sticky
  class:start={edge === "start"}
  class:end={edge === "end"}
  class:right={align === "right"}
  class:clickable={sortable}
  class:gradient-start={gradientFade === "start"}
  class:shadow
  tabindex={colIndex}
  on:click
  on:keypress={handleKeyPress}
  bind:clientWidth={colWidth}
  bind:this={divElement}
>
  <div class="wrapper" in:fade|global={CELL_FADE_BASE_ANIM_CONFIG}>
    <slot />
    {#if sortable}
      <div
        style:display={align === "right" ? (sortDirection ? "block" : "none") : "block"}
        style:visibility={sortDirection ? "visible" : "hidden"}
      >
        <Icon
          name="sort"
          direction={sortDirection ? (sortDirection === "asc" ? "n" : "s") : undefined}
          --color="var(--rk-neutral-4)"
          --display="block"
        />
      </div>
    {/if}
  </div>
</div>

<style>
  .base {
    display: flex;
    align-items: center;
    background: var(--rk-neutral-0);
    border-top: 1px solid var(--rk-divider-0);
    border-bottom: 1px solid var(--rk-divider-0);
    height: var(--col-header-height);
    justify-content: var(--justify);
    padding-left: 12px;
    padding-right: 12px;
    color: var(--color);
    cursor: var(--cursor);
  }

  .base:hover {
    color: var(--hover-color);
  }

  .wrapper {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .start {
    border-start-start-radius: var(--rk-rounding-s);
    border-end-start-radius: var(--rk-rounding-s);
    border-left: 1px solid var(--rk-divider-0);
  }

  .end {
    border-start-end-radius: var(--rk-rounding-s);
    border-end-end-radius: var(--rk-rounding-s);
    border-right: 1px solid var(--rk-divider-0);
    box-shadow: 3px 0px 0px 1px #fff, 2px -2px 0px 0px #fff;
  }

  .right {
    justify-content: flex-end;
  }

  .gradient-start::before {
    content: "";
    width: 30px;
    top: 0;
    bottom: 0;
    left: -30px;
    display: block;
    position: absolute;
    background-image: linear-gradient(
      to left,
      rgba(248, 248, 248, 1) 0%,
      rgba(248, 248, 248, 0) 100%
    );
  }

  .sticky {
    position: sticky;
    left: var(--sticky-left);
    right: var(--sticky-right);
    top: var(--sticky-top);
    z-index: var(--z-index);
  }

  .clickable {
    cursor: pointer;
  }

  .shadow::after {
    content: "";
    position: absolute;
    top: 0;
    left: 100%;
    bottom: 0;
    display: block;
    width: var(--scroll-gradient-width);
    will-change: width;
    background: linear-gradient(
      270deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.01) 38.87%,
      rgba(0, 0, 0, 0.02) 60.71%,
      rgba(0, 0, 0, 0.03) 75.41%,
      rgba(0, 0, 0, 0.03) 90.08%,
      rgba(0, 0, 0, 0.04) 97.27%,
      rgba(0, 0, 0, 0.06) 99.46%,
      rgba(0, 0, 0, 0.11) 100%
    );
    transition: width 0.2s ease-in-out;
  }
</style>
